// src/features/system/orchestrator/components/CreateWorkerDialog.tsx
import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AppDialog from '@/shared/components/ui/AppDialog';
import { CreateWorkerInput } from '../api/workersApi';
import { StartWorkflowResponse, triggerWorkflowV2Api } from '../api/workflowsV2Api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WORKER_KIND_OPTIONS = [
  'http-handler',
  'pubsub-handler',
  'device-handler',
];

const TRANSPORT_OPTIONS = [
  'http',
  'pubsub',
  'internal',
];

// Avoid direct import.meta access — CRA/webpack treats it as a Critical dependency
// warning, which fails CI builds (CI=true → warnings are errors).
const META_ENV: Record<string, string | undefined> =
  (typeof process !== 'undefined' ? (process.env as Record<string, string | undefined>) : {});
const PUBSUB_PROJECT_ID =
  META_ENV.VITE_PUBSUB_PROJECT_ID ||
  META_ENV.REACT_APP_PUBSUB_PROJECT_ID ||
  META_ENV.PUBSUB_PROJECT_ID ||
  'local-dev';
const PUBSUB_TOPIC_PREFIX =
  META_ENV.VITE_PUBSUB_TOPIC_PREFIX ||
  META_ENV.REACT_APP_PUBSUB_TOPIC_PREFIX ||
  META_ENV.PUBSUB_TOPIC_PREFIX ||
  '';

const CREATE_WORKER_WORKFLOW_KEY =
  META_ENV.VITE_ORCHESTRATOR_CREATE_WORKER_WORKFLOW_KEY ||
  META_ENV.REACT_APP_ORCHESTRATOR_CREATE_WORKER_WORKFLOW_KEY ||
  META_ENV.ORCHESTRATOR_CREATE_WORKER_WORKFLOW_KEY ||
  'system.worker.create';

const DEFAULT_FORM: CreateWorkerInput & { region?: string } = {
  worker_key: '',
  worker_kind: 'pubsub-handler',
  transport : 'pubsub',
  endpoint  : '',
  region    : '',
  is_active : true,
};

export default function CreateWorkerDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);
  const [manualEndpoint, setManualEndpoint] = useState('');

  const mutation = useMutation<StartWorkflowResponse, Error, CreateWorkerInput>({
    mutationFn: (payload) => triggerWorkflowV2Api({
      workflow_key: CREATE_WORKER_WORKFLOW_KEY,
      inputs: payload as unknown as Record<string, unknown>,
    }),
    onSuccess: () => {
      // Create is workflow-driven; the worker may appear a moment after the workflow is started.
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workers'] });
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workers'] });
      }, 1500);
      setForm(DEFAULT_FORM);
      setError(null);
      setManualEndpoint('');
      onOpenChange(false);
    },
    onError: (err) => setError(err?.message || 'Failed to create worker'),
  });

  const setField = <K extends keyof CreateWorkerInput>(key: K, value: CreateWorkerInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setRegion = (value: string) => {
    setForm((prev) => ({ ...prev, region: value }));
  };

  const buildPubsubEndpoint = React.useCallback((workerKey: string) => {
    const key = workerKey?.trim() || 'worker';
    const topic = `${PUBSUB_TOPIC_PREFIX}${key}`;
    return `projects/${PUBSUB_PROJECT_ID}/topics/${topic}`;
  }, []);

  const isPubsub = form.transport === 'pubsub';

  React.useEffect(() => {
    if (isPubsub) {
      const auto = buildPubsubEndpoint(form.worker_key);
      if (form.endpoint !== auto) {
        setForm((prev) => ({ ...prev, endpoint: auto }));
      }
    } else if (form.endpoint !== manualEndpoint) {
      setForm((prev) => ({ ...prev, endpoint: manualEndpoint }));
    }
  }, [isPubsub, form.worker_key, form.endpoint, manualEndpoint, buildPubsubEndpoint]);

  const handleManualEndpointChange = (value: string) => {
    setManualEndpoint(value);
    setField('endpoint', value);
  };

  const handleTransportChange = (value: string) => {
    setField('transport', value as CreateWorkerInput['transport']);
  };

  const trimmed = useMemo(() => ({
    ...form,
    region: form.region?.trim() || undefined,
  }), [form]);

  const canSave =
    trimmed.worker_key.trim().length > 0 &&
    trimmed.worker_kind.trim().length > 0 &&
    trimmed.transport.trim().length > 0 &&
    trimmed.endpoint.trim().length > 0 &&
    !mutation.isPending;

  const handleConfirm = () => {
    if (!canSave) return;
    mutation.mutate({ ...trimmed });
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setForm(DEFAULT_FORM);
      setError(null);
    }
    onOpenChange(next);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={handleClose}
      title="Register worker"
      description="Workers are wrappers around orchestrator transports (HTTP, Pub/Sub, etc.)."
      size="md"
      onConfirm={handleConfirm}
      confirmLabel={mutation.isPending ? 'Creating…' : 'Create worker'}
      confirmDisabled={!canSave}
      isBusy={mutation.isPending}
    >
      <div className="space-y-4">
        {error && <p className="text-sm text-rose-400">{error}</p>}

        <LabeledInput
          label="Worker Key *"
          value={form.worker_key}
          placeholder="media_ingest_worker"
          onChange={(v) => setField('worker_key', v)}
        />

        <LabeledSelect
          label="Worker Kind *"
          value={form.worker_kind}
          options={WORKER_KIND_OPTIONS}
          description="Used by orchestrator to route steps to the correct worker type."
          onChange={(v) => setField('worker_kind', v as CreateWorkerInput['worker_kind'])}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LabeledSelect
            label="Transport *"
            value={form.transport}
            options={TRANSPORT_OPTIONS}
            onChange={handleTransportChange}
          />
          <LabeledInput
            label="Region"
            value={form.region || ''}
            placeholder="us-central1"
            onChange={setRegion}
          />
        </div>

        {isPubsub ? (
          <LabeledInput
            label="Endpoint (auto)"
            value={form.endpoint}
            description={`Derived from project ${PUBSUB_PROJECT_ID}.`}
            placeholder="projects/.../topics/..."
            onChange={() => {}}
            readOnly
          />
        ) : (
          <LabeledInput
            label="Endpoint *"
            value={form.endpoint}
            placeholder="https://worker-host/api/orchestrator"
            onChange={handleManualEndpointChange}
          />
        )}

        <label className="flex items-center gap-2 text-sm text-neutral-200">
          <input
            type="checkbox"
            className="accent-emerald-500"
            checked={form.is_active ?? true}
            onChange={(e) => setField('is_active', e.target.checked)}
          />
          Worker is active immediately
        </label>
      </div>
    </AppDialog>
  );
}

interface LabeledInputProps {
  label: string;
  value: string;
  placeholder?: string;
  description?: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

function LabeledInput({ label, value, placeholder, description, onChange, readOnly }: LabeledInputProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs uppercase tracking-wide text-neutral-400">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-70"
        readOnly={readOnly}
        disabled={readOnly}
      />
      {description && <p className="text-[11px] leading-snug text-neutral-500">{description}</p>}
    </div>
  );
}

interface LabeledSelectProps {
  label: string;
  value: string;
  options: string[];
  description?: string;
  onChange: (value: string) => void;
}

function LabeledSelect({ label, value, options, description, onChange }: LabeledSelectProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs uppercase tracking-wide text-neutral-400">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-black border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      {description && <p className="text-[11px] leading-snug text-neutral-500">{description}</p>}
    </div>
  );
}
